import React, { useState, useEffect } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { getIreferCredentials } from '../../services/ireferService';

const SearchGuidelines = () => {
  const { user, profile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    checkCredentials();
  }, [user]);

  const checkCredentials = async () => {
    if (!user?.id) return;

    try {
      const data = await getIreferCredentials(user?.id);
      setHasCredentials(data?.verified && data?.autoLogin);
    } catch (error) {
      console.error('Error checking credentials:', error);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery?.trim()) return;

    setSearching(true);
    setSearchError('');
    try {
      const { data: results, error } = await supabase
        ?.from('irefer_mappings')
        ?.select('*')
        ?.or(`title.ilike.%${searchQuery}%,clinical_scenario.ilike.%${searchQuery}%,body_region.ilike.%${searchQuery}%,modality.ilike.%${searchQuery}%`)
        ?.order('irefer_code')
        ?.limit(20);

      if (error) throw error;
      setSearchResults(results || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Unable to search guidelines at this time. Please try again shortly.');
    } finally {
      setSearching(false);
    }
  };

  const openGuideline = (guideline) => {
    window.open(guideline?.irefer_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role || 'clinician'} />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-foreground mb-2 flex items-center gap-3">
              <Icon name="Search" size={32} color="var(--color-primary)" />
              🔍 Search Clinical Guidelines
            </h1>
            <p className="text-muted-foreground">
              Search iRefer.co.uk guidelines by clinical scenario, body region, or modality
            </p>
          </div>

          {/* iRefer Branding */}
          <div className="irefer-branding bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="BookOpen" size={24} color="white" />
              </div>
              <div>
                <strong className="text-lg text-foreground block mb-1">Powered by iRefer.co.uk</strong>
                <p className="text-sm text-muted-foreground">
                  Royal College of Radiologists - Evidence-Based Guidelines
                </p>
              </div>
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="search-form mb-8">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Icon
                  name="Search"
                  size={20}
                  color="var(--color-muted-foreground)"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search... (e.g., 'knee pain', 'headache', 'MRI brain')"
                  className="w-full pl-12 pr-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button type="submit" disabled={searching} iconName="Search">
                {searching ? 'Searching...' : '🔍 Search'}
              </Button>
            </div>
          </form>

          {/* Search Error */}
          {searchError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{searchError}</p>
            </div>
          )}

          {/* Credentials Notice */}
          {!hasCredentials && (
            <div className="credentials-notice bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8 flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-warning)" />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  💡 <a href="/account-settings?tab=irefer" className="text-primary hover:underline font-medium">Connect iRefer account</a> for quick access to guidelines
                </p>
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchResults?.length > 0 && (
            <div className="search-results">
              <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
                📚 Results ({searchResults?.length})
              </h3>
              <div className="results-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchResults?.map((guideline) => (
                  <div
                    key={guideline?.id}
                    className="guideline-card bg-card border border-border rounded-lg p-5 hover:shadow-md transition-all"
                  >
                    <div className="card-header flex items-center justify-between mb-3">
                      <span className="irefer-code-badge bg-primary text-primary-foreground px-3 py-1 rounded-lg text-sm font-semibold">
                        {guideline?.irefer_code}
                      </span>
                      <span className="evidence-badge bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                        Grade {guideline?.evidence_grade}
                      </span>
                    </div>

                    <h4 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
                      {guideline?.title}
                    </h4>

                    <div className="meta flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <span>{guideline?.category}</span>
                      {guideline?.subcategory && (
                        <>
                          <span>›</span>
                          <span>{guideline?.subcategory}</span>
                        </>
                      )}
                    </div>

                    <div className="details mb-3">
                      <span className="text-sm text-foreground">
                        <strong>Recommended:</strong> {guideline?.modality}
                      </span>
                    </div>

                    <p className="scenario text-xs text-muted-foreground mb-4 line-clamp-3">
                      {guideline?.clinical_scenario}
                    </p>

                    <button
                      onClick={() => openGuideline(guideline)}
                      className="btn-view w-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-between group"
                    >
                      <span>
                        📖 {hasCredentials ? 'View (Quick Access)' : 'View on iRefer.co.uk'} ↗
                      </span>
                      <Icon
                        name="ExternalLink"
                        size={16}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchResults?.length === 0 && searchQuery && !searching && (
            <div className="no-results bg-card border border-border rounded-lg p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Guidelines Found</h3>
              <p className="text-sm text-muted-foreground">
                No results for "{searchQuery}". Try different keywords.
              </p>
            </div>
          )}

          {/* Search Tips */}
          <div className="search-tips bg-muted rounded-lg p-6 mt-8">
            <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Lightbulb" size={18} color="var(--color-primary)" />
              💡 Search Tips
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span><strong>Clinical terms:</strong> "acute abdomen", "knee pain"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span><strong>Body region:</strong> "brain", "spine", "chest"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span><strong>Modality:</strong> "MRI", "CT", "ultrasound"</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchGuidelines;